import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@epihub/web/src/_ctx/theme';

type Props = {
  text: string;
  width?: string;
  height?: string;
  fontSize?: string;
  duration?: number;
  showLoadingText?: boolean;
};

export const WaveText = ({
  text,
  width = '100%',
  height = '100vh',
  fontSize = '8rem',
  duration = 3000,
  showLoadingText = true,
}: Props) => {
  const [wavePath, setWavePath] = useState('M -0.1,1.1 L -0.1,1 L 1.1,1 L 1.1,1.1 Z');
  const [loadingPercent, setLoadingPercent] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const clipId = useRef(`wave-clip-${Math.random().toString(36).substr(2, 9)}`).current;

  useEffect(() => {
    const animateWave = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setWavePath(createWavePath(easedProgress, timestamp));
      setLoadingPercent(Math.round(easedProgress * 100));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateWave);
      } else {
        setTimeout(() => {
          startTimeRef.current = null;
          animationRef.current = requestAnimationFrame(animateWave);
        }, 1000);
      }
    };

    animationRef.current = requestAnimationFrame(animateWave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [duration]);

  return (
    <WaveTextContainer width={width} height={height}>
      <HiddenSvg xmlns='http://www.w3.org/2000/svg'>
        <defs>
          <clipPath id={clipId} clipPathUnits='objectBoundingBox'>
            <path d={wavePath} />
          </clipPath>
        </defs>
      </HiddenSvg>

      <WaveTextWrapper>
        <TextOutline fontSize={fontSize}>{text}</TextOutline>
        <TextFill fontSize={fontSize} clipId={clipId}>{text}</TextFill>
        {showLoadingText && <LoadingText>Loading... {loadingPercent}%</LoadingText>}
      </WaveTextWrapper>
    </WaveTextContainer>
  );
};

const WaveTextContainer = styled.div<{ width: string; height: string }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  background-color: #0a0a0a;
`;

const WaveTextWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TextBase = styled.span<{ fontSize?: string }>`
  font-size: ${(props) => props.fontSize || '8rem'};
  font-weight: 900;
  font-family: 'Arial Black', 'Helvetica Neue', sans-serif;
  letter-spacing: -0.02em;
  white-space: nowrap;
`;

const TextOutline = styled(TextBase)`
  color: transparent;
  -webkit-text-stroke: 2px rgba(255, 255, 255, 0.3);
`;

const TextFill = styled(TextBase)<{ clipId: string }>`
  position: absolute;
  top: 0;
  left: 0;
  color: #ffffff;
  clip-path: url(#${(props) => props.clipId});
  -webkit-clip-path: url(#${(props) => props.clipId});
`;

const HiddenSvg = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const LoadingText = styled.div`
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;
  font-weight: 400;
  font-family: system-ui, sans-serif;
`;

function createWavePath(progress: number, time: number) {
  const waveHeight = 0.08 * (1 - progress);
  const waveFrequency = 2;
  const waveSpeed = time * 0.003;
  const waterLevel = 1 - progress;

  let path = `M -0.1,1.1 L -0.1,${waterLevel}`;

  const segments = 24;
  for (let i = 0; i <= segments; i++) {
    const x = -0.1 + (i / segments) * 1.2;
    const wave = Math.sin((x * waveFrequency * Math.PI * 2) + waveSpeed) * waveHeight;
    const y = waterLevel + wave;
    path += ` L ${x.toFixed(3)},${y.toFixed(3)}`;
  }

  path += ' L 1.1,1.1 L -0.1,1.1 Z';

  return path;
}
